import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useBooth } from '../../../../../hooks/useBooth';
import useMessaging from '../../../../../hooks/useMessaging';
import { usePermission } from '../../../../../hooks/usePermission';
import { Booth, CategoryFilterValues, LinkedPerson, PDFFile } from '../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import InlineEditButton from '../../../pageBuilder/InlineEditButton';
import AppointmentBoard from '../../VystemLight/appointment/AppointmentBoard';
import NetworkRoom from '../../VystemLight/network/NetworkRoom';
import Section from '../../VystemLight/oneonone/BigBoothTwoColsSection';
import BoothLinkedPerson from '../../VystemLight/oneonone/BoothLinkedPerson';
import ChildBooth from '../../VystemLight/oneonone/ChildBooth';
import MeetingroomTemplate from '../MeetingRoomTemplate';
import SlickSlider from '../Slider';
import Link from 'next/link';

interface BoothPreviewInterface {
  booth: Booth;
  filters?: any;
  isEditMode: boolean;
  setIsOpen: (value: boolean) => void;
  setViewerOpen: (value: boolean) => void;
  setIsEditMode: (value: boolean) => void;
}
const BoothPreview = ({ booth, filters, isEditMode, setIsOpen, setViewerOpen, setIsEditMode }: BoothPreviewInterface) => {
  const [filterValues, setFilterValues] = useState<CategoryFilterValues[]>([]);

  const event = useSelector((state) => state.common.event);
  const slots = useSelector((state) => state.common.slots);
  const networks = useSelector((state) => state.common.networks);
  const user = useSelector((state) => state.common.user);

  const { t } = useTranslation(['booth']);

  const { openMessagingConversationFromAttendee } = useMessaging();
  const { checkAppointmentsExist } = useBooth();
  const { isBoothHost } = usePermission();

  useEffect(() => {
    // We only consider the first filter as this template should only work with one filter
    filters && filters.length > 0 && setFilterValues(filters[0].categoryFilterValues);
  }, [filters]);

  const prettifyDescription = (description: string) => {
    let newDesc = '';
    try {
      newDesc = description.replace(/<br>/g, '').replace(/<li>/g, '<li> &ndash; ');
    } catch (error) {
      console.error(error);
    }
    return newDesc;
  };

  const meetingTypeMeetingrooms = booth?.meetingrooms;
  const myPresentations = slots?.filter((slot) => slot.meetingroom.booth._id === booth?._id).map((slot) => slot.meetingroom);
  const meetingTypeConferencerooms = booth?.conferencerooms;
  const sections = [
    {
      title: event?.template.individualNames.meetingrooms || t('booth:chatrooms'),
      meetingrooms: meetingTypeMeetingrooms,
      enabled: true,
    },
    {
      title: event?.template.individualNames.conferencerooms || t('booth:chatrooms'),
      meetingrooms: meetingTypeConferencerooms,
      enabled: true,
    },
    {
      title: event?.template.individualNames.presentationrooms || t('booth:chatrooms'),
      meetingrooms: myPresentations,
      enabled: booth?.settings.displayPresentationsInBooths.value,
    },
  ];

  const onLinkedPersonClick = (user: LinkedPerson['user']) => {
    openMessagingConversationFromAttendee(user);
  };

  const categoryStrings = filterValues
    .filter((f) => booth.categoryFilterValues.includes(f._id))
    .map((f, idx, arr) => `${f.value}${idx + 1 < arr.length ? `, ` : ``}`);

  const boothName = filterValues?.length > 0 ? categoryStrings : booth.name;
  const boothNetworks = networks.filter((network) => network?.booth?._id === booth._id);
  return (
    <div className="font-xs px-4 py-8 max-h-[80vh] overflow-y-auto">
      <div className="flex text-[#87a8d8] text-base">
        <p className="text-3xl">{boothName}</p>
        {isBoothHost(user, [booth]) ? (
          <InlineEditButton isEditMode={isEditMode} setIsEditMode={setIsEditMode} forceShow tooltip={t('pagebuilder:booths-edit-details')} />
        ) : (
          <FontAwesomeIcon style={{ color: 'black', cursor: 'pointer', marginLeft: 'auto' }} icon={faTimes} onClick={() => setIsOpen(false)} />
        )}
      </div>

      <p className="w-full border-b-2 font-3xl text-left text-[#1a3363] p-1 hyphens-manual" dangerouslySetInnerHTML={{ __html: booth?.slogan ?? '' }} />
      {booth?.boothVideos && booth?.headerImages && booth?.headerImages?.length > 0 && (
        <div className="px-8 mx-4 border-b-2">
          <SlickSlider
            viewerOpen={setViewerOpen}
            welcomeVideo={booth?.boothVideos?.length > 0 ? booth?.boothVideos[0] : undefined}
            slides={booth?.headerImages || []}
            withViewer
          />
        </div>
      )}

      {booth.description && (
        <div className="w-full px-4 text-[#1a3363] text-sm border-b-2">
          <div className="content-text" dangerouslySetInnerHTML={{ __html: prettifyDescription(booth?.description) }} />
        </div>
      )}

      {booth.childBooths && booth.childBooths.length > 0 && (
        <Section title={event?.template.individualNames.subBooths || ''}>
          {booth.childBooths.map((booth) => (
            <ChildBooth key={booth._id} booth={booth} />
          ))}
        </Section>
      )}
      {event?.settings?.isLinkedBoothMembersActive && booth.linkedPeople && booth.linkedPeople.length > 0 && (
        <>
          <h1 className="mt-4 mb-4 text-xl font-bold">{event?.template.individualNames.linkedPeople ?? t('booth:linked-people')}</h1>

          <Section>
            {booth.linkedPeople &&
              booth.linkedPeople
                .map((person) => ({ ...person, avatar: person.avatar ?? person.user.avatar, name: person.name ?? person.user.name }))
                .map((person) => <BoothLinkedPerson person={person} onLinkedPersonClick={onLinkedPersonClick} />)}
          </Section>
        </>
      )}

      {sections.map((section) => {
        if (section && section?.meetingrooms && section?.meetingrooms?.length > 0 && section.enabled)
          return (
            <div key={section.title} className="w-full px-4 text-[#1a3363] text-sm border-b-2">
              <Section title={section.title}>
                {section?.meetingrooms?.map((meetingroom) => {
                  if (booth) return <MeetingroomTemplate key={meetingroom._id} meetingroom={meetingroom} booth={booth} />;
                })}
              </Section>
            </div>
          );
      })}
      {event?.modules?.booking?.active && booth?.settings?.enableAppointments.value && checkAppointmentsExist(booth?.meetingrooms) && (
        <>
          <Section title={event.template.individualNames.appointments}>
            <div className="w-full pt-2 pb-4 overflow-auto">
              <AppointmentBoard event={event} date={event.datetime_start} endDate={event.datetime_end} meetingrooms={booth?.meetingrooms} bookingmode />
            </div>
          </Section>
        </>
      )}
      {booth?.settings?.displayNetworkingroomsInBooth?.value && boothNetworks.length > 0 && (
        <>
          <h1 className="mt-4 mb-4 text-xl font-bold text-gray-700">{t('booth:networking-rooms')}</h1>
          {boothNetworks.map((network) => (
            <div className="flex justify-center w-full pt-2 pb-4 overflow-auto">
              <NetworkRoom key={network._id} network={network} />
            </div>
          ))}
        </>
      )}
      <div className="flex justify-start w-full px-4 m-auto text-center">
        {booth.websiteUrl && (
          <button className="px-1 py-4 mr-6 text-center border" onClick={() => window.open(booth.websiteUrl, '_blank')}>
            Website
          </button>
        )}

        {booth.pdfUploads?.map((pdf: PDFFile, idx: any, array: any) => (
          <Link key={pdf._id} className="px-1 py-4 mr-6 text-center border" href={createDisplayLink(pdf.file)} target="_blank">
            {array.length < 2 ? 'Weitere Infos (PDF)' : pdf.name}
          </Link>
        ))}
      </div>
    </div>
  );
};
export default BoothPreview;
